import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DialogService,
  DynamicDialogModule,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';

import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import {
  PlcAlarmCode,
  PlcAlarmCodeSortOrQuery,
} from 'src/app/model/station-logs/plc-alarm-code';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { StationLogApiService } from 'src/app/services/api/station-log-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { TabService } from 'src/app/core/services/tab.service';
import { TROUBLESHOOTING_SEARCH_TABLE_DEFINITION } from 'src/app/operational-monitoring/definitions/troubleshooting-search-table-definition';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { StationLogTableService } from 'src/app/services/table/station-log-table.service';
import { TroubleshootingDialogComponent } from 'src/app/operational-monitoring/components/troubleshooting-dialog/troubleshooting-dialog/troubleshooting-dialog.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { PlcAlarmCodeTableService } from 'src/app/services/table/plc-alarm-code-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata } from '@openng/optimus-ui/api';
import { ProgressSpinnerComponent } from '../../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../../core/components/general-grid.component';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { ButtonModule } from '@openng/optimus-ui/button';

@Component({
  selector: 'app-troubleshooting-search-container',
  imports: [
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslateModule,
    DynamicDialogModule,
    BlockUIModule,
    ButtonModule,
  ],
  templateUrl: './troubleshooting-search-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './troubleshooting-search-container.component.scss',
})
export class TroubleshootingSearchContainerComponent
  extends SearchContainerComponent<PlcAlarmCode>
  implements OnInit, OnDestroy
{
  protected _plcAlarmCodeTableService = inject(PlcAlarmCodeTableService);
  protected _translateService = inject(TranslateService);
  protected _authApi = inject(AuthApiService);
  protected _stationLogApi = inject(StationLogApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _dialogService = inject(DialogService);
  protected _tableService = inject(StationLogTableService);
  protected _tabService = inject(TabService);

  ref: DynamicDialogRef<TroubleshootingDialogComponent> | null = null;
  pageLoading = signal(true);
  alarmCodes: WritableSignal<PlcAlarmCode[]> = signal([]);
  canCreate = signal(false);

  activeTabChangedSub!: Subscription;

  ngOnInit(): void {
    this._plcAlarmCodeTableService.setLastLazyLoad(null);
    this.setTableService(this._plcAlarmCodeTableService);
    this.columns = TROUBLESHOOTING_SEARCH_TABLE_DEFINITION;

    const screenName$ = this._translateService.get(
      'OPMONITORING.SCREEN.TROUBLESHOOTING'
    );
    forkJoin([screenName$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
    });

    this.canCreate.set(
      this._authApi.doesUserHaveClaim(
        CLAIMS.FIELD_OPERATIONS.PLC_ALARM_CODES.CREATE_PLC_ALARM_CODES
      )
    );

    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    this._plcAlarmCodeTableService.setLastLazyLoad($event.lazy);
    const query: PlcAlarmCodeSortOrQuery = {
      alarmCode: undefined,
      alarmComment: undefined,
      alarmExplanation: undefined,
      alarmFix: undefined,
    };

    if (filters.alarmCode) {
      query.alarmCode = (filters.alarmCode as FilterMetadata).value;
    }
    if (filters.alarmComment) {
      query.alarmComment = (filters.alarmComment as FilterMetadata).value;
    }
    if (filters.alarmExplanation) {
      query.alarmExplanation = (
        filters.alarmExplanation as FilterMetadata
      ).value;
    }
    if (filters.alarmFix) {
      query.alarmFix = (filters.alarmFix as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'alarmCode');
    this.pageLoading.set(true);
    this._stationLogApi
      .queryForPlcAlarmCodes(req.page, req.pageSize, query, sort)
      .subscribe({
        next: (response) => {
          this.alarmCodes.set(response.items);
          this.totalRecords = response.totalRecords;
          this.elements = [...this.alarmCodes()];
          this.pageLoading.set(false);
        },
        error: (error) => {
          this.pageLoading.set(false);
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.QUERY_PLC_ALARM_CODES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  async changeViewEvent($event: { data: PlcAlarmCode }): Promise<void> {
    const dialogEditHeader = await firstValueFrom(
      this._translateService.get(
        'OPMONITORING.MESSAGES.HEADERS.EDIT_PLC_ALARM_CODE'
      )
    );
    this.ref = this._dialogService.open(TroubleshootingDialogComponent, {
      header: dialogEditHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: {
        alarmCode: $event.data,
        formType: 'Edit',
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the part
        // so we don't need to refresh the page
        return;
      }
      this.refresh();
    });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._plcAlarmCodeTableService.getLastLazyLoad()! });
  }

  async newAlarmCode(): Promise<void> {
    const dialogCreateHeader = await firstValueFrom(
      this._translateService.get(
        'OPMONITORING.MESSAGES.HEADERS.CREATE_PLC_ALARM_CODE'
      )
    );
    this.ref = this._dialogService.open(TroubleshootingDialogComponent, {
      header: dialogCreateHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      data: {
        alarmCode: null,
        formType: 'Create',
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If there's no data provided in the onclose, we didn't actually do anything to the alarm code
        // so we don't need to refresh the page
        return;
      }
      this.refresh();
    });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
